import { useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

export type SheetSnap = "peek" | "mid" | "full";
export const SNAPS: SheetSnap[] = ["peek", "mid", "full"];

/**
 * A phone's panel: a sheet under the map that is pulled taller or shorter by
 * its handle, and settles at one of three heights. A tap on the handle goes to
 * the next height; the arrow keys do the same.
 */
export function LiveSheet({
  snap,
  onSnap,
  heights,
  label,
  children,
}: {
  snap: SheetSnap;
  onSnap: (s: SheetSnap) => void;
  heights: Record<SheetSnap, number>;
  label: string;
  children: ReactNode;
}) {
  const { t } = useTranslation();
  const [drag, setDrag] = useState<number | null>(null);
  // The latest height while dragging: the release must not read a height from a render behind.
  const start = useRef<{ y: number; h: number; moved: boolean; now: number } | null>(null);
  const height = drag ?? heights[snap];
  const nearest = (h: number) => SNAPS.reduce((best, s) => (Math.abs(heights[s] - h) < Math.abs(heights[best] - h) ? s : best), "peek" as SheetSnap);
  const step = (dir: 1 | -1) => onSnap(SNAPS[Math.min(SNAPS.length - 1, Math.max(0, SNAPS.indexOf(snap) + dir))]);
  return (
    <section className={`live-sheet${drag !== null ? " is-dragging" : ""}`} style={{ height }} aria-label={label} data-snap={snap}>
      <button
        type="button"
        className="live-sheet-handle"
        aria-label={snap === "full" ? t("live.sheet.smaller") : t("live.sheet.bigger")}
        onPointerDown={(e) => {
          e.currentTarget.setPointerCapture(e.pointerId);
          start.current = { y: e.clientY, h: heights[snap], moved: false, now: heights[snap] };
        }}
        onPointerMove={(e) => {
          const s = start.current;
          if (!s) return;
          const dy = s.y - e.clientY;
          if (Math.abs(dy) > 6) s.moved = true;
          if (!s.moved) return;
          s.now = Math.max(heights.peek - 40, Math.min(heights.full, s.h + dy));
          setDrag(s.now);
        }}
        onPointerUp={() => {
          const s = start.current;
          start.current = null;
          if (!s) return;
          if (s.moved) onSnap(nearest(s.now));
          else onSnap(snap === "full" ? "peek" : SNAPS[SNAPS.indexOf(snap) + 1]);
          setDrag(null);
        }}
        onPointerCancel={() => {
          start.current = null;
          setDrag(null);
        }}
        onClick={(e) => {
          // Enter or Space (a press of the pointer was handled above).
          if (e.detail === 0) onSnap(snap === "full" ? "peek" : SNAPS[SNAPS.indexOf(snap) + 1]);
        }}
        onKeyDown={(e) => {
          if (e.key === "ArrowUp") step(1);
          else if (e.key === "ArrowDown") step(-1);
          else return;
          e.preventDefault();
        }}
      >
        <span className="live-sheet-grip" aria-hidden="true" />
      </button>
      <div className="live-sheet-body">{children}</div>
    </section>
  );
}
